/**
 * Firebase の接続設定(docs/03_architecture.md §6)。
 *
 * **`VITE_FIREBASE_*` が揃っている時だけ** Firebase モードで動く。
 * 未設定なら `readFirebaseEnv()` が `null` を返し、アプリは現行のローカルモード
 * (localStorage + クライアント抽選)のまま起動する。判定はここ1か所だけ。
 *
 * 必須:
 *   VITE_FIREBASE_API_KEY
 *   VITE_FIREBASE_AUTH_DOMAIN
 *   VITE_FIREBASE_PROJECT_ID
 *   VITE_FIREBASE_APP_ID
 * 任意:
 *   VITE_FIREBASE_STORAGE_BUCKET / VITE_FIREBASE_MESSAGING_SENDER_ID
 *   VITE_FIREBASE_FUNCTIONS_REGION      既定 asia-northeast1(functions/src/options.ts と揃える)
 *   VITE_FIREBASE_EMULATOR=true         エミュレータへ接続する
 *   VITE_FIREBASE_EMULATOR_HOST         既定 127.0.0.1
 *   VITE_FIREBASE_AUTH_EMULATOR_PORT    既定 9099(firebase/firebase.json と揃える)
 *   VITE_FIREBASE_FUNCTIONS_EMULATOR_PORT 既定 5001
 *   VITE_FIREBASE_APPCHECK_SITE_KEY     App Check(reCAPTCHA Enterprise)のサイトキー
 *   VITE_FIREBASE_APPCHECK_DEBUG_TOKEN  App Check のデバッグトークン(開発・CI用)
 *
 * App Check のキーは判定(REQUIRED_KEYS)には入れない。未設定でも Firebase モードは
 * そのまま動き、`initializeAppCheck` を呼ばないだけ — 「client配線 → 鍵登録 →
 * functions強制ON」という段階的ロールアウトの順序を保つため。
 */

/** Cloud Functions のデプロイ先。`functions/src/options.ts` の REGION と一致させる。 */
export const DEFAULT_FUNCTIONS_REGION = 'asia-northeast1';

export const DEFAULT_EMULATOR_HOST = '127.0.0.1';
export const DEFAULT_AUTH_EMULATOR_PORT = 9099;
export const DEFAULT_FUNCTIONS_EMULATOR_PORT = 5001;

export interface EmulatorConfig {
  readonly host: string;
  readonly authPort: number;
  readonly functionsPort: number;
}

export interface FirebaseEnv {
  readonly apiKey: string;
  readonly authDomain: string;
  readonly projectId: string;
  readonly appId: string;
  readonly storageBucket?: string;
  readonly messagingSenderId?: string;
  readonly functionsRegion: string;
  /** reCAPTCHA Enterprise のサイトキー。未設定なら App Check を初期化しない。 */
  readonly appCheckSiteKey?: string;
  /** App Check のデバッグトークン。設定すると reCAPTCHA を通さず検証を通せる(開発用)。 */
  readonly appCheckDebugToken?: string;
  /** null ならクラウドの本番プロジェクトに接続する。 */
  readonly emulator: EmulatorConfig | null;
}

export type EnvSource = Readonly<Record<string, string | undefined>>;

const REQUIRED_KEYS = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_APP_ID',
] as const;

/**
 * 既定の設定ソース。
 *
 * ブラウザ/Vite ビルドでは `import.meta.env`、Node(エミュレータ統合テスト)では
 * `process.env` を見る。両方あれば `import.meta.env` を優先する。
 */
export function defaultEnvSource(): EnvSource {
  const viteEnv = (import.meta.env ?? {}) as EnvSource;
  const nodeEnv =
    (globalThis as { process?: { env?: EnvSource } }).process?.env ?? ({} as EnvSource);
  return { ...nodeEnv, ...viteEnv };
}

function trimmed(source: EnvSource, key: string): string | undefined {
  const value = source[key];
  if (typeof value !== 'string') return undefined;
  const text = value.trim();
  return text === '' ? undefined : text;
}

function port(source: EnvSource, key: string, fallback: number): number {
  const raw = trimmed(source, key);
  if (raw === undefined) return fallback;
  const value = Number.parseInt(raw, 10);
  return Number.isInteger(value) && value > 0 && value < 65536 ? value : fallback;
}

function isTrue(value: string | undefined): boolean {
  return value === 'true' || value === '1';
}

/** エミュレータ設定。`VITE_FIREBASE_EMULATOR=true` のときだけ返す。 */
export function readEmulatorConfig(source: EnvSource = defaultEnvSource()): EmulatorConfig | null {
  if (!isTrue(trimmed(source, 'VITE_FIREBASE_EMULATOR'))) return null;
  return {
    host: trimmed(source, 'VITE_FIREBASE_EMULATOR_HOST') ?? DEFAULT_EMULATOR_HOST,
    authPort: port(source, 'VITE_FIREBASE_AUTH_EMULATOR_PORT', DEFAULT_AUTH_EMULATOR_PORT),
    functionsPort: port(
      source,
      'VITE_FIREBASE_FUNCTIONS_EMULATOR_PORT',
      DEFAULT_FUNCTIONS_EMULATOR_PORT,
    ),
  };
}

/**
 * 必須キーが**全て**揃っていれば設定を返す。1つでも欠けたら null(= ローカルモード)。
 *
 * 「一部だけ設定されている」は設定ミスの可能性が高いので、
 * 呼び出し側が気付けるように `missingFirebaseEnvKeys()` で欠けを報告できるようにしてある。
 */
export function readFirebaseEnv(source: EnvSource = defaultEnvSource()): FirebaseEnv | null {
  const values = REQUIRED_KEYS.map((key) => trimmed(source, key));
  if (values.some((value) => value === undefined)) return null;
  const [apiKey, authDomain, projectId, appId] = values as [string, string, string, string];

  const storageBucket = trimmed(source, 'VITE_FIREBASE_STORAGE_BUCKET');
  const messagingSenderId = trimmed(source, 'VITE_FIREBASE_MESSAGING_SENDER_ID');
  const appCheckSiteKey = trimmed(source, 'VITE_FIREBASE_APPCHECK_SITE_KEY');
  const appCheckDebugToken = trimmed(source, 'VITE_FIREBASE_APPCHECK_DEBUG_TOKEN');

  return {
    apiKey,
    authDomain,
    projectId,
    appId,
    ...(storageBucket === undefined ? {} : { storageBucket }),
    ...(messagingSenderId === undefined ? {} : { messagingSenderId }),
    ...(appCheckSiteKey === undefined ? {} : { appCheckSiteKey }),
    ...(appCheckDebugToken === undefined ? {} : { appCheckDebugToken }),
    functionsRegion: trimmed(source, 'VITE_FIREBASE_FUNCTIONS_REGION') ?? DEFAULT_FUNCTIONS_REGION,
    emulator: readEmulatorConfig(source),
  };
}

/** 設定途中(一部だけ書かれている)を検出するための欠けキー一覧。 */
export function missingFirebaseEnvKeys(source: EnvSource = defaultEnvSource()): string[] {
  return REQUIRED_KEYS.filter((key) => trimmed(source, key) === undefined);
}

/** 1つでも `VITE_FIREBASE_*` が書かれているか(= Firebase を使う意図があるか)。 */
export function hasAnyFirebaseEnv(source: EnvSource = defaultEnvSource()): boolean {
  return REQUIRED_KEYS.some((key) => trimmed(source, key) !== undefined);
}
